import { createContext, useContext, useMemo } from 'react';
import { createContextualCan } from '@casl/react';
import { useMsal } from '@azure/msal-react';
import {
  defineSuperAdminAbility,
  type AppAbility,
  type SuperAdminRole,
} from './superadmin';

/**
 * Maps Entra app-role display names (as they appear in the id-token `roles`
 * claim) to the canonical SuperAdminRole strings used by the ability factory.
 *
 * App role names are set in the Task 1 app registration. Keep this map in
 * sync with the role values configured there.
 */
const ROLE_MAP: Record<string, SuperAdminRole> = {
  SuperAdmin: 'super_admin',
  SuperAdminReadOnly: 'super_admin_readonly',
};

/**
 * The CASL AbilityContext. Exported so tests can inject a specific ability
 * (see `apps/console/__tests__/renderInApp.tsx`). Production code uses
 * `AbilityProvider` (below) + `Can` / `useAbility` and should not read this
 * directly.
 */
export const AbilityContext = createContext<AppAbility>(
  undefined as unknown as AppAbility,
);

/**
 * `<Can>` component bound to the super-admin AbilityContext.
 * Use instead of importing from @casl/react directly — this one
 * already reads the correct context.
 *
 * Example:
 *   <Can I="manage" a="Company">
 *     <CreateCompanyButton />
 *   </Can>
 *   <Can I="read" a="User">
 *     <UserList />
 *   </Can>
 */
export const Can = createContextualCan(AbilityContext.Consumer);

export function useAbility(): AppAbility {
  return useContext(AbilityContext);
}

/**
 * Maps a signed-in account's id-token claims to a CASL ability: reads the
 * `roles` claim, maps each to a SuperAdminRole via ROLE_MAP, and builds the
 * ability via `defineSuperAdminAbility`.
 *
 * Extracted from `AbilityProvider` (below) so the same claims→ability logic
 * can be reused wherever an ability must be built outside the React tree —
 * e.g. the Refine `accessControlProvider` getter in App.tsx, which reads the
 * msal singleton directly rather than through `useMsal()`.
 *
 * NOTE: The `roles` claim is an id-token app-role claim emitted by the
 * Task 1 app registration. If the access token carries roles instead,
 * decode it (JWT) and read from there. Confirm with the Task-1 token
 * capture before shipping.
 */
export function buildAbilityFromClaims(
  claims: { roles?: string[]; oid?: string; email?: string } | undefined,
): AppAbility {
  const roles = (claims?.roles ?? [])
    .map((r) => ROLE_MAP[r])
    .filter((r): r is SuperAdminRole => !!r);

  return defineSuperAdminAbility({
    roles,
    oid: claims?.oid ?? '',
    email: claims?.email,
  });
}

/**
 * Reads the signed-in account's `roles` id-token claim, maps them to
 * SuperAdminRole values, builds a CASL ability, and provides it via
 * AbilityContext.
 *
 * Must be rendered inside <MsalProvider>. Rebuilds the ability whenever
 * the active accounts list changes (e.g. after login/logout).
 */
export function AbilityProvider({ children }: { children: React.ReactNode }) {
  const { accounts } = useMsal();

  const ability = useMemo(
    () =>
      buildAbilityFromClaims(
        accounts[0]?.idTokenClaims as
          | { roles?: string[]; oid?: string; email?: string }
          | undefined,
      ),
    [accounts],
  );

  return (
    <AbilityContext.Provider value={ability}>
      {children}
    </AbilityContext.Provider>
  );
}
